import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The label sits above the photo, aligned on its left edge, so the media box
 * has to match the rendered image exactly — otherwise the label lines up with
 * the cell's letterbox slack instead of the photo. Giving the box the image's
 * natural aspect ratio makes it shrink-wrap the photo at any cell size.
 */
export function useAspectRatioMedia() {
    const mediaRef = useRef<HTMLDivElement | null>(null);

    const applyAspectRatio = useCallback((img: HTMLImageElement) => {
        const { naturalWidth, naturalHeight } = img;
        if (!mediaRef.current || naturalWidth <= 0 || naturalHeight <= 0) return;
        mediaRef.current.style.aspectRatio = `${naturalWidth} / ${naturalHeight}`;
    }, []);

    // Cached images can finish loading before React attaches onLoad, so the ref
    // callback covers the already-complete case.
    const imageRef = useCallback(
        (img: HTMLImageElement | null) => {
            if (img?.complete) applyAspectRatio(img);
        },
        [applyAspectRatio]
    );

    const handleImageLoad = useCallback(
        (e: React.SyntheticEvent<HTMLImageElement>) => applyAspectRatio(e.currentTarget),
        [applyAspectRatio]
    );

    return { mediaRef, imageRef, handleImageLoad };
}

/**
 * Local draft so typing doesn't rebuild the grid node on every keystroke (a
 * rebuild would remount the cell and drop input focus). Committed on blur /
 * Enter; re-synced if the persisted caption changes underneath us.
 */
export function useCaptionDraft(
    caption: string,
    cellIndex: number,
    onCaptionChange: (cellIndex: number, caption: string) => void
) {
    const [captionDraft, setCaptionDraft] = useState(caption);

    useEffect(() => {
        setCaptionDraft(caption);
    }, [caption]);

    const commitCaption = useCallback(() => {
        if (captionDraft !== caption) onCaptionChange(cellIndex, captionDraft);
    }, [captionDraft, caption, cellIndex, onCaptionChange]);

    return { captionDraft, setCaptionDraft, commitCaption };
}
